import { hslToRgb, rgbToHex } from "@mui/material/styles";

/**
 * Generates a consistent color based on a string ID
 * @param id - The ID to generate color for
 * @returns HSL color string
 */
const generateColorFromId = (id: string): string => {
	let hash = 0;
	for (let i = 0; i < id.length; i++) {
		const char = id.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash;
	}
	
	const hue = Math.abs(hash) % 360;
	
	const saturation = 70;
	const lightness = 50;

	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const hslToHex = (hsl: string): string => {
    if (hsl.startsWith('hsl(')) {
      const rgb = hslToRgb(hsl.toString());
      return rgbToHex(rgb).toUpperCase();
    }
    return hsl.toUpperCase();
  };

/**
 * Converts a hex color to HSL
 * @param hex - Hex color string (e.g., "#ff5733")
 * @returns HSL color string
 */

const hexToHsl = (hex: string): string => {
	const cleanHex = hex.replace('#', '');
	
	const r = parseInt(cleanHex.substr(0, 2), 16) / 255;
	const g = parseInt(cleanHex.substr(2, 2), 16) / 255;
	const b = parseInt(cleanHex.substr(4, 2), 16) / 255;
	
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;
	
	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		
		switch (max) {
			case r: h = (g - b) / d + (g < b ? 6 : 0); break;
			case g: h = (b - r) / d + 2; break;
			case b: h = (r - g) / d + 4; break;
		}
		h /= 6;
	}
	
	return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
};

/**
 * Generates a lighter version of the color for backgrounds
 * @param id - The ID to generate color for
 * @returns HSL color string with higher lightness
 */
const generateLightColorFromId = (id: string): string => {
	let hash = 0;
	for (let i = 0; i < id.length; i++) {
		const char = id.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash;
	}
	
	const hue = Math.abs(hash) % 360;
	
	const saturation = 30;
	const lightness = 85;
	
	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Creates a lighter version of an HSL color for backgrounds
 * @param hslColor - HSL color string
 * @returns Lighter HSL color string
 */
const createLightColorFromHsl = (hslColor: string): string => {
	// Extract hue from HSL string
	const hueMatch = hslColor.match(/hsl\((\d+),/);
	if (!hueMatch) return hslColor;
	
	const hue = parseInt(hueMatch[1]);
	
	// Use lighter saturation and lightness for background colors
	const saturation = 30; // 30% saturation for subtle background
	const lightness = 85; // 85% lightness for light background
	
	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Gets the color for a user, prioritizing custom colors over hash-generated ones
 * @param userId - The user ID
 * @param customColors - Record of custom colors by user ID (from organizations.profile_colors)
 * @returns HSL color string
 */
export const getUserColor = (userId: string, customColors?: Record<string, string>): string => {
	// Check for custom color first
	if (customColors && customColors[userId]) {
		return customColors[userId];
	}
	
	// Fallback to hash-generated color
	return hslToHex(generateColorFromId(userId));
};

/**
 * Gets the light background color for a user
 * @param userId - The user ID
 * @param customColors - Record of custom colors by user ID (from organizations.profile_colors)
 * @returns Light HSL color string
 */
export const getUserLightColor = (userId: string, customColors?: Record<string, string>): string => {
	// Check for custom color first
	if (customColors && customColors[userId]) {
		const customColor = customColors[userId];
		const hslColor = hexToHsl(customColor);
		return hslToHex(createLightColorFromHsl(hslColor));
	}
	
	// Fallback to hash-generated light color
	return hslToHex(generateLightColorFromId(userId));
};